import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, EyeOutlined, ReloadOutlined, SearchOutlined, WalletOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getServiceProviderDepositRefundRequestDetail,
  listServiceProviderDepositRefundRequests,
  reviewServiceProviderDepositRefundRequest,
  type ServiceProviderDepositRefundRequestItem,
  type ServiceProviderDepositRefundRequestStatus,
} from '../../services/serviceProviderDepositRefundRequestService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface SearchFormValues {
  keyword?: string;
  status?: ServiceProviderDepositRefundRequestStatus | 'all';
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

interface ReviewFormValues {
  action: 'approve' | 'reject';
  remark?: string;
}

const statusOptions = [
  { label: '全部', value: 'all' },
  { label: '待审核', value: 'pending_review' },
  { label: '已通过', value: 'approved' },
  { label: '已驳回', value: 'rejected' },
];

const statusMeta: Record<string, { color: string; text: string }> = {
  pending_review: { color: 'processing', text: '待审核' },
  approved: { color: 'success', text: '已通过' },
  rejected: { color: 'error', text: '已驳回' },
};

const moneyFormatter = (value?: number) =>
  `￥${Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const renderStatus = (status: string, fallback?: string) => {
  const meta = statusMeta[status] || { color: 'default', text: fallback || status || '-' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
};

const StaffDepositRefundRequest: React.FC = () => {
  const [searchForm] = Form.useForm<SearchFormValues>();
  const [reviewForm] = Form.useForm<ReviewFormValues>();
  const reviewAction = Form.useWatch('action', reviewForm);

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [items, setItems] = useState<ServiceProviderDepositRefundRequestItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<ServiceProviderDepositRefundRequestItem | null>(null);
  const tableWrapRef = useHorizontalTableWheel();

  const loadData = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize) => {
      setLoading(true);
      try {
        const values = searchForm.getFieldsValue();
        const response = await listServiceProviderDepositRefundRequests({
          page,
          pageSize,
          keyword: values.keyword || undefined,
          status: values.status || 'pending_review',
          startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
        });
        setItems(response.list || []);
        setPagination({ page, pageSize, total: response.total || 0 });
      } catch {
        message.error('加载保证金提现申请失败');
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.pageSize, searchForm],
  );

  useEffect(() => {
    void loadData(1, 10);
  }, [loadData]);

  const loadDetail = async (id: string, openDrawer = true) => {
    setDetailLoading(true);
    try {
      const detail = await getServiceProviderDepositRefundRequestDetail(id);
      setCurrentDetail(detail);
      if (openDrawer) setDetailOpen(true);
      return detail;
    } catch {
      message.error('加载保证金提现申请详情失败');
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = () => {
    void loadData(1, pagination.pageSize);
  };

  const handleStatusChange = (event: RadioChangeEvent) => {
    searchForm.setFieldsValue({ status: event.target.value || 'all' });
    void loadData(1, pagination.pageSize);
  };

  const handleReset = () => {
    searchForm.setFieldsValue({ keyword: undefined, status: 'pending_review', dateRange: undefined });
    void loadData(1, pagination.pageSize);
  };

  const handleOpenReview = async (record: ServiceProviderDepositRefundRequestItem) => {
    let detail = currentDetail;
    if (!detail || detail.id !== record.id) {
      detail = await loadDetail(record.id, false);
    }
    if (!detail) return;
    reviewForm.setFieldsValue({ action: 'approve', remark: '' });
    setReviewOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!currentDetail) return;
    try {
      const values = await reviewForm.validateFields();
      setReviewSubmitting(true);
      const detail = await reviewServiceProviderDepositRefundRequest(currentDetail.id, {
        action: values.action,
        remark: values.remark?.trim() || '',
      });
      setCurrentDetail(detail);
      setReviewOpen(false);
      message.success(values.action === 'approve' ? '保证金退还审核通过' : '保证金退还申请已驳回');
      void loadData();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '提交审核失败');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const columns: ColumnsType<ServiceProviderDepositRefundRequestItem> = [
    {
      title: '申请单号',
      dataIndex: 'requestNo',
      width: 190,
      fixed: 'left',
      render: (value: string) => <Text copyable>{value || '-'}</Text>,
    },
    {
      title: '服务人员',
      dataIndex: 'providerName',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{record.providerName || '-'}</div>
          <Text type="secondary">{record.providerPhone || '-'}</Text>
        </div>
      ),
    },
    {
      title: '申请金额',
      dataIndex: 'amount',
      width: 130,
      render: (amount: number) => <Text strong type="danger">{moneyFormatter(amount)}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (status: string, record) => renderStatus(status, record.statusText),
    },
    {
      title: '申请时间',
      dataIndex: 'requestedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '审核时间',
      dataIndex: 'reviewedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '审核备注',
      dataIndex: 'reviewRemark',
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => void loadDetail(record.id)}>
            详情
          </Button>
          {record.status === 'pending_review' && (
            <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => void handleOpenReview(record)}>
              审核
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <Space>
            <WalletOutlined />
            <span>保证金提现申请</span>
          </Space>
        )}
        extra={<Button icon={<ReloadOutlined />} onClick={() => void loadData()}>刷新</Button>}
      >
        <Form
          form={searchForm}
          layout="inline"
          initialValues={{ status: 'pending_review' }}
          style={{ marginBottom: 16, rowGap: 12 }}
        >
          <Form.Item name="keyword" label="关键词">
            <Input allowClear placeholder="申请单号 / 姓名 / 手机号" style={{ width: 260 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Radio.Group optionType="button" buttonStyle="solid" options={statusOptions} onChange={handleStatusChange} />
          </Form.Item>
          <Form.Item name="dateRange" label="申请时间">
            <RangePicker allowClear />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <div ref={tableWrapRef}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={items}
            scroll={{ x: 1200 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: total => `共 ${total} 条`,
              onChange: (page, pageSize) => void loadData(page, pageSize),
            }}
          />
        </div>
      </Card>

      <Drawer title="保证金提现申请详情" open={detailOpen} onClose={() => setDetailOpen(false)} size={720}>
        <Card loading={detailLoading} size="small">
          {currentDetail && (
            <Descriptions column={2} size="small">
              <Descriptions.Item label="申请单号">{currentDetail.requestNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">{renderStatus(currentDetail.status, currentDetail.statusText)}</Descriptions.Item>
              <Descriptions.Item label="服务人员">{currentDetail.providerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentDetail.providerPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="申请金额">{moneyFormatter(currentDetail.amount)}</Descriptions.Item>
              <Descriptions.Item label="当前可提现余额">{moneyFormatter(currentDetail.withdrawableNow)}</Descriptions.Item>
              <Descriptions.Item label="申请时保证金">{moneyFormatter(currentDetail.beforeAmount)}</Descriptions.Item>
              <Descriptions.Item label="审核后保证金">{moneyFormatter(currentDetail.afterAmount)}</Descriptions.Item>
              <Descriptions.Item label="申请时可提现">{moneyFormatter(currentDetail.beforeBalance)}</Descriptions.Item>
              <Descriptions.Item label="审核后可提现">{moneyFormatter(currentDetail.afterBalance)}</Descriptions.Item>
              <Descriptions.Item label="申请时间">{currentDetail.requestedAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="审核时间">{currentDetail.reviewedAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="审核人" span={2}>{currentDetail.reviewerName || currentDetail.reviewerId || '-'}</Descriptions.Item>
              <Descriptions.Item label="申请备注" span={2}>{currentDetail.applyRemark || '-'}</Descriptions.Item>
              <Descriptions.Item label="审核备注" span={2}>{currentDetail.reviewRemark || '-'}</Descriptions.Item>
            </Descriptions>
          )}
        </Card>
        {currentDetail?.status === 'pending_review' && (
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => void handleOpenReview(currentDetail)}>
              提交审核
            </Button>
          </div>
        )}
      </Drawer>

      <Modal
        title="保证金提现申请审核"
        open={reviewOpen}
        onCancel={() => setReviewOpen(false)}
        onOk={handleSubmitReview}
        confirmLoading={reviewSubmitting}
        okText="提交审核"
        cancelText="取消"
        width={640}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" title="申请信息">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="服务人员">{currentDetail?.providerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentDetail?.providerPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="申请金额">{moneyFormatter(currentDetail?.amount)}</Descriptions.Item>
              <Descriptions.Item label="申请时保证金">{moneyFormatter(currentDetail?.beforeAmount)}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Form form={reviewForm} layout="vertical" initialValues={{ action: 'approve', remark: '' }}>
            <Form.Item name="action" label="审核结果" rules={[{ required: true, message: '请选择审核结果' }]}>
              <Radio.Group>
                <Radio value="approve">通过并划转到可提现余额</Radio>
                <Radio value="reject">驳回并保留保证金账户</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="remark"
              label="审核备注"
              extra={reviewAction === 'reject' ? '驳回时必须填写原因。' : '通过时可选填备注。'}
              rules={[
                {
                  validator: async (_, value) => {
                    if (reviewAction === 'reject' && !String(value || '').trim()) {
                      throw new Error('驳回时请填写审核备注');
                    }
                  },
                },
              ]}
            >
              <Input.TextArea rows={4} maxLength={255} showCount placeholder="请输入审核备注" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </div>
  );
};

export default StaffDepositRefundRequest;
